/**
 * ConfirmDialog — substitui window.confirm()
 *
 * Uso:
 *   import { useConfirm } from '../components/ConfirmDialog';
 *   const confirm = useConfirm();
 *   const ok = await confirm({ title: 'Excluir?', message: 'Esta ação não pode ser desfeita.', danger: true });
 *   if (ok) { ... }
 */

import React, { createContext, useContext, useState, useCallback } from 'react';
import styled from 'styled-components';
import { AlertTriangle } from 'lucide-react';
import { theme } from '../styles/theme';
import { Button, ModalOverlay, ModalBox, ModalHeader, ModalTitle, ModalBody, ModalFooter } from './ui';

const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null); // { title, message, danger, resolve }

  const confirm = useCallback(({ title, message, danger = false, confirmLabel, cancelLabel }) => {
    return new Promise(resolve => {
      setState({ title, message, danger, confirmLabel, cancelLabel, resolve });
    });
  }, []);

  const handleClose = (result) => {
    state?.resolve(result);
    setState(null);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && (
        <ModalOverlay onClick={() => handleClose(false)}>
          <ModalBox maxWidth="400px" onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <TitleRow>
                {state.danger && <AlertTriangle size={18} color={theme.colors.danger} />}
                <ModalTitle>{state.title}</ModalTitle>
              </TitleRow>
            </ModalHeader>
            {state.message && (
              <ModalBody style={{ paddingTop: theme.spacing[4], paddingBottom: theme.spacing[4] }}>
                <Message $danger={state.danger}>{state.message}</Message>
              </ModalBody>
            )}
            <ModalFooter>
              <Button variant="secondary" onClick={() => handleClose(false)}>
                {state.cancelLabel || 'Cancelar'}
              </Button>
              <Button variant={state.danger ? 'danger' : 'primary'} onClick={() => handleClose(true)}>
                {state.confirmLabel || (state.danger ? 'Excluir' : 'Confirmar')}
              </Button>
            </ModalFooter>
          </ModalBox>
        </ModalOverlay>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used inside ConfirmProvider');
  return ctx;
}

const TitleRow = styled.div`display: flex; align-items: center; gap: ${theme.spacing[2]};`;
const Message = styled.p`
  margin: 0;
  font-size: ${theme.typography.sm};
  color: ${p => p.$danger ? theme.colors.danger : theme.colors.textSecondary};
  line-height: 1.5;
`;
